import React, { useEffect, useState } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import { supabase } from '../lib/supabase';
import { Loader2, Download, CheckCircle, Circle, Trash2, ArrowLeft } from 'lucide-react';
import ImageGalleryOverlay, { GalleryImage } from '../components/architecture/ImageGalleryOverlay';
import { useNavigate } from 'react-router-dom';

interface GeneratedImage {
    name: string;
    url: string;
    created_at: string;
}

const ArchitectureGalleryPage: React.FC = () => {
    const navigate = useNavigate();
    const [images, setImages] = useState<GeneratedImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState<string | null>(null);
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [overlayIndex, setOverlayIndex] = useState<number | null>(null);
    const [isSelectionMode, setIsSelectionMode] = useState(false);

    useEffect(() => {
        const getUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) setUserId(user.id);
        };
        getUser();
    }, []);

    const fetchImages = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            // Fetch all images
            const { data, error } = await supabase.storage
                .from('generated-images')
                .list(userId + '/', {
                    limit: 100, // Reasonable limit for now
                    sortBy: { column: 'created_at', order: 'desc' },
                });

            if (error) {
                console.error('Error fetching images:', error);
                return;
            }

            if (data) {
                const loadedImages = data
                    .filter(file => file.name !== '.emptyFolderPlaceholder')
                    .map(file => {
                        const { data: { publicUrl } } = supabase.storage
                            .from('generated-images')
                            .getPublicUrl(`${userId}/${file.name}`);

                        return {
                            name: file.name,
                            url: publicUrl,
                            created_at: file.created_at,
                        };
                    });
                setImages(loadedImages);
            }
        } catch (err) {
            console.error('Unexpected error fetching gallery:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) fetchImages();
    }, [userId]);

    const toggleSelection = (name: string) => {
        const newSelection = new Set(selectedImages);
        if (newSelection.has(name)) {
            newSelection.delete(name);
        } else {
            newSelection.add(name);
        }
        setSelectedImages(newSelection);
        // Auto-enable selection mode if first item selected
        if (!isSelectionMode && newSelection.size > 0) {
            setIsSelectionMode(true);
        }
        // Auto-disable if empty (optional, keeping it manual might be better UX for some)
        if (isSelectionMode && newSelection.size === 0) {
            setIsSelectionMode(false);
        }
    };

    const handleBulkDownload = async () => {
        // Sequential download for now as we don't have JSZip
        const imagesToDownload = images.filter(img => selectedImages.has(img.name));

        for (const img of imagesToDownload) {
            try {
                const response = await fetch(img.url);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = img.name;
                document.body.appendChild(link);
                link.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);

                // Small delay to prevent browser throttling
                await new Promise(resolve => setTimeout(resolve, 500));
            } catch (error) {
                console.error('Download failed for', img.name, error);
            }
        }
        // Deselect all after download? Maybe not.
    };

    const handleSelectAll = () => {
        if (selectedImages.size === images.length) {
            setSelectedImages(new Set());
            setIsSelectionMode(false);
        } else {
            const allNames = new Set(images.map(img => img.name));
            setSelectedImages(allNames);
            setIsSelectionMode(true);
        }
    };

    return (
        <DashboardLayout>
            <div className="flex flex-col h-screen bg-stone-950 text-zinc-100 font-sans">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-stone-800 bg-stone-950/50 backdrop-blur-md sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/app/architecture')}
                            className="p-2 rounded-full hover:bg-stone-800 text-stone-400 hover:text-white transition-all"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-stone-500 bg-clip-text text-transparent">
                            Architecture Gallery
                        </h1>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Selection Controls */}
                        {images.length > 0 && (
                            <>
                                <button
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isSelectionMode
                                        ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                                        : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                                        }`}
                                    onClick={(e) => {
                                        setIsSelectionMode(!isSelectionMode);
                                        if (isSelectionMode) setSelectedImages(new Set());
                                    }}
                                >
                                    {isSelectionMode ? 'Done Selecting' : 'Select Images'}
                                </button>

                                {isSelectionMode && (
                                    <>
                                        <button
                                            onClick={handleSelectAll}
                                            className="px-4 py-2 rounded-lg text-sm font-medium bg-stone-800 text-stone-300 hover:bg-stone-700 transition-all"
                                        >
                                            {selectedImages.size === images.length ? 'Deselect All' : 'Select All'}
                                        </button>

                                        {selectedImages.size > 0 && (
                                            <button
                                                onClick={handleBulkDownload}
                                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-black hover:bg-zinc-200 transition-all shadow-lg shadow-white/10"
                                            >
                                                <Download size={16} />
                                                Download ({selectedImages.size})
                                            </button>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 relative">
                    {loading && images.length === 0 ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 size={48} className="animate-spin text-amber-500" />
                        </div>
                    ) : images.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-600">
                            <p className="text-xl font-medium mb-2">No images generated yet</p>
                            <p className="text-sm">Start generating from the Architecture Workspace</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {images.map((img, idx) => {
                                const isSelected = selectedImages.has(img.name);
                                return (
                                    <div
                                        key={img.name}
                                        className={`
                                            group relative aspect-square rounded-xl overflow-hidden cursor-pointer border-2 transition-all duration-200
                                            ${isSelected
                                                ? 'border-amber-500 ring-2 ring-amber-500/20 scale-[0.98]'
                                                : isSelectionMode
                                                    ? 'border-transparent opacity-60 hover:opacity-100 hover:border-stone-700'
                                                    : 'border-transparent hover:border-stone-700 hover:shadow-xl'
                                            }
                                        `}
                                        onClick={() => {
                                            if (isSelectionMode) {
                                                toggleSelection(img.name);
                                            } else {
                                                setOverlayIndex(idx);
                                            }
                                        }}
                                    >
                                        <img
                                            src={img.url}
                                            alt={img.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            loading="lazy"
                                        />

                                        {/* Selection Checkbox */}
                                        {(isSelectionMode || isSelected) && (
                                            <div className="absolute top-3 right-3 z-10">
                                                <div className={`
                                                    w-6 h-6 rounded-full flex items-center justify-center backdrop-blur-md transition-all
                                                    ${isSelected ? 'bg-amber-500 text-black' : 'bg-black/50 text-white/50 hover:bg-black/70'}
                                                `}>
                                                    {isSelected ? <CheckCircle size={16} /> : <Circle size={16} />}
                                                </div>
                                            </div>
                                        )}
                                        {/* Hover Overlay (only in view mode) */}
                                        {!isSelectionMode && (
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex flex-col justify-end p-3 opacity-0 group-hover:opacity-100">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="text-xs text-white/70 font-mono truncate">
                                                        {new Date(img.created_at).toLocaleDateString()}
                                                    </div>
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                const res = await fetch(img.url);
                                                                const blob = await res.blob();
                                                                const url = window.URL.createObjectURL(blob);
                                                                const link = document.createElement('a');
                                                                link.href = url;
                                                                link.download = img.name;
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                window.URL.revokeObjectURL(url);
                                                                document.body.removeChild(link);
                                                            } catch {
                                                                const link = document.createElement('a');
                                                                link.href = img.url;
                                                                link.download = img.name;
                                                                link.target = '_blank';
                                                                link.click();
                                                            }
                                                        }}
                                                        className="flex-shrink-0 p-1.5 rounded-lg bg-white/10 hover:bg-white/25 text-white transition-all backdrop-blur border border-white/10"
                                                        title="Download image"
                                                    >
                                                        <Download size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Full Screen Overlay */}
                <ImageGalleryOverlay
                    isOpen={overlayIndex !== null}
                    onClose={() => setOverlayIndex(null)}
                    images={images.map(i => ({ url: i.url, name: i.name } as GalleryImage))}
                    initialIndex={overlayIndex || 0}
                />
            </div>
        </DashboardLayout>
    );
};

export default ArchitectureGalleryPage;
